import { useLocalization } from "@fluent/react"
import { unwrapResult } from "@reduxjs/toolkit"
import AddIcon from "@mui/icons-material/Add"
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined"
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined"
import LaunchIcon from "@mui/icons-material/Launch"
import PrintIcon from "@mui/icons-material/Print"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess"
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore"
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Stack, TableContainer, Tooltip, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView"
import { TreeItem } from "@mui/x-tree-view/TreeItem"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"
import { CancelButton } from "../../../app/components/CancelButton"
import { LpField } from "../../../app/components/LpField"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DocumentReportPrinter } from "../components/DocumentReportPrinter"
import { DocumentDownloadStatsDto, loadDocumentDownloadStats, loadDocumentsForStructureNode, StructureNodeDocumentDto } from "../documentsQmsApi"
import { selectSettings } from "../../settings/settingsSlice"
import { StructureDefinitionSelect } from "../../structures/components/StructureDefinitionSelect"
import { StructureDefinitionDto, StructureNodeTreeDto, StructureTreeDto, loadCompanyStructuresDefinitions, loadStructureTree } from "../../structures/structureSlice"
import { CreateDocumentQMSDialog } from "./dialogs/CreateDocumentQMSDialog"

interface StructureTreeNodeItemProps {
    node: StructureNodeTreeDto
}

function findNodeById(nodes: StructureNodeTreeDto[], nodeId: string): StructureNodeTreeDto | null {
    for (const node of nodes) {
        if (node.id === nodeId) {
            return node
        }

        const childFound = findNodeById(node.children, nodeId)
        if (childFound) {
            return childFound
        }
    }

    return null
}

function collectExpandableNodeIds(nodes: StructureNodeTreeDto[]): string[] {
    return nodes.flatMap(node => {
        const nestedIds = collectExpandableNodeIds(node.children)
        return node.children.length > 0 ? [node.id, ...nestedIds] : nestedIds
    })
}

function StructureTreeNodeItem({ node }: StructureTreeNodeItemProps) {
    const label = `${node.code ? `${node.code} - ` : ""}${node.name}`
    const hasChildren = node.children.length > 0

    return (
        <TreeItem
            itemId={node.id}
            label={(
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25, minWidth: 0 }}>
                    {hasChildren ? (
                        <FolderOutlinedIcon sx={{ fontSize: 18, color: "warning.main" }} />
                    ) : (
                        <SettingsOutlinedIcon sx={{ fontSize: 17, color: "action.active" }} />
                    )}

                    <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.2 }} noWrap>
                        {label}
                    </Typography>
                </Stack>
            )}
        >
            {node.children.map(child => (
                <StructureTreeNodeItem key={child.id} node={child} />
            ))}
        </TreeItem>
    )
}

export const QmsDocumentsList = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [documents, setDocuments] = useState<StructureNodeDocumentDto[]>([])
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
    const [createQMSDocument, setCreateQMSDocument] = useState(false)
    const [printDocumentId, setPrintDocumentId] = useState<string | null>(null)
    const [structures, setStructures] = useState<StructureDefinitionDto[]>([])
    const [tree, setTree] = useState<StructureTreeDto | null>(null)
    const [selectedStructureId, setSelectedStructureId] = useState<string | null>(null)
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
    const [expanded, setExpanded] = useState<string[]>([])
    const [isStructuresLoading, setIsStructuresLoading] = useState(false)
    const [isTreeLoading, setIsTreeLoading] = useState(false)
    const [structuresError, setStructuresError] = useState<string | null>(null)
    const [treeError, setTreeError] = useState<string | null>(null)
    const [statsDocument, setStatsDocument] = useState<StructureNodeDocumentDto | null>(null)
    const [documentStats, setDocumentStats] = useState<DocumentDownloadStatsDto | null>(null)
    const [isStatsLoading, setIsStatsLoading] = useState(false)
    const [statsError, setStatsError] = useState<string | null>(null)

    const selectedStructure = useMemo(
        () => structures.find(structure => structure.id === selectedStructureId) ?? null,
        [selectedStructureId, structures],
    )

    const selectedNode = useMemo(
        () => selectedNodeId && tree ? findNodeById(tree.roots, selectedNodeId) : null,
        [selectedNodeId, tree],
    )

    const currentNodeDocuments = useMemo(
        () => selectedNodeId ? documents.filter(document => document.linkedNodeId === selectedNodeId) : [],
        [documents, selectedNodeId],
    )

    const inheritedDocuments = useMemo(
        () => selectedNodeId ? documents.filter(document => document.linkedNodeId !== selectedNodeId) : [],
        [documents, selectedNodeId],
    )

    const loadDocumentsForNode = useCallback((nodeId: string) => {
        setIsLoadingDocuments(true)

        dispatch(loadDocumentsForStructureNode({ nodeId, includeParents: true }))
            .then(unwrapResult)
            .then(setDocuments)
            .catch(error => {
                console.error(error)
                setDocuments([])
            })
            .finally(() => setIsLoadingDocuments(false))
    }, [dispatch])

    const openDocumentStats = useCallback((document: StructureNodeDocumentDto) => {
        setStatsDocument(document)
        setDocumentStats(null)
        setStatsError(null)
        setIsStatsLoading(true)

        dispatch(loadDocumentDownloadStats({ documentId: document.documentId.toString() }))
            .then(unwrapResult)
            .then(setDocumentStats)
            .catch(() => {
                setStatsError(l10n.getString("document-download-stats-load-error", null, "Nie udało się załadować statystyk pobrań dokumentu."))
            })
            .finally(() => setIsStatsLoading(false))
    }, [dispatch, l10n])

    const loadStructures = useCallback(() => {
        setIsStructuresLoading(true)
        setStructuresError(null)

        dispatch(loadCompanyStructuresDefinitions({ onlyActive: true }))
            .then(unwrapResult)
            .then(result => {
                setStructures(result)
                setSelectedStructureId(currentId => {
                    if (currentId && result.some(structure => structure.id === currentId)) {
                        return currentId
                    }

                    return result[0]?.id ?? null
                })
            })
            .catch(() => {
                setStructuresError(l10n.getString("qms-templates-structures-load-error", null, "Nie udało się załadować struktur."))
            })
            .finally(() => setIsStructuresLoading(false))
    }, [dispatch, l10n])

    useEffect(() => {
        loadStructures()
    }, [loadStructures])

    useEffect(() => {
        if (!selectedStructureId) {
            setTree(null)
            setTreeError(null)
            setSelectedNodeId(null)
            setExpanded([])
            return
        }

        setIsTreeLoading(true)
        setTreeError(null)
        setSelectedNodeId(null)

        dispatch(loadStructureTree({ structureDefinitionId: selectedStructureId }))
            .then(unwrapResult)
            .then(loadedTree => {
                setTree(loadedTree)
                setExpanded(loadedTree.roots.map(root => root.id))
            })
            .catch(() => {
                setTree(null)
                setTreeError(l10n.getString("qms-templates-tree-load-error", null, "Nie udało się załadować drzewa wybranej struktury."))
            })
            .finally(() => setIsTreeLoading(false))
    }, [dispatch, selectedStructureId, l10n])

    useEffect(() => {
        if (!selectedNodeId) {
            setDocuments([])
            return
        }

        loadDocumentsForNode(selectedNodeId)
    }, [selectedNodeId, loadDocumentsForNode])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText
        if (locale === "pl") {
            return plPL.components.MuiDataGrid.defaultProps.localeText
        }

        return defaultEnLocaleText
    }

    const buildColumns = (rows: StructureNodeDocumentDto[], includeSourceColumn = false): GridColDef[] => {
        const columns: GridColDef[] = [
            {
                field: "lp",
                headerName: l10n.getString("lp", null, "Lp"),
                width: 80,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                renderCell: params => (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <LpField value={rows.findIndex(document => document.documentId === params.row.documentId) + 1} />
                    </Box>
                ),
            },
            {
                field: "documentNumber",
                headerName: l10n.getString("document-number", null, "Numer dokumentu"),
                width: 220,
            },
            {
                field: "title",
                headerName: l10n.getString("title", null, "Tytuł"),
                width: 420,
                flex: 1,
            },
            {
                field: "documentType",
                headerName: l10n.getString("type", null, "Typ"),
                width: 180,
            },
        ]

        if (includeSourceColumn) {
            columns.push({
                field: "linkedNodeName",
                headerName: l10n.getString("qms-structure-source-node", null, "Pochodzi z"),
                width: 240,
            })
        }

        columns.push(
            {
                field: "status",
                headerName: l10n.getString("document-status", null, "Status"),
                width: 150,
                valueGetter: (_, row) => {
                    const status = row.status?.toLowerCase() ?? ""
                    return status ? l10n.getString("document-status-" + status, null, "") : ""
                },
            },
            {
                field: "language",
                headerName: l10n.getString("document-language", null, "Język dokumentu"),
                width: 180,            },
            {
                field: "actions",
                headerName: l10n.getString("actions", null, "Akcje"),
                width: 150,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                disableColumnMenu: true,
                renderCell: params => (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                            <Tooltip title={l10n.getString("go-to", null, "Przejdź")}>
                                <IconButton size="small" onClick={() => navigate(`/document/edit/${params.row.documentId}`)}>
                                    <LaunchIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={l10n.getString("print", null, "Wydruk")}>
                                <IconButton size="small" onClick={() => setPrintDocumentId(params.row.documentId)}>
                                    <PrintIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={l10n.getString("document-download-stats", null, "Statystyki pobrań")}>
                                <IconButton size="small" onClick={() => openDocumentStats(params.row)}>
                                    <AnalyticsOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                ),
            },
        )

        return columns
    }

    const currentNodeColumns = buildColumns(currentNodeDocuments)
    const inheritedColumns = buildColumns(inheritedDocuments, true)

    if (isStructuresLoading && structures.length === 0) {
        return <LoadingContainer />
    }

    return (
        <>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
                <Stack spacing={2} sx={{ width: { xs: "100%", lg: 420 }, flexShrink: 0 }}>
                    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                        <Box sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.100" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {l10n.getString("qms-templates-selected-structure", null, "Wybrana struktura")}
                            </Typography>
                        </Box>

                        {structuresError ? (
                            <Box sx={{ p: 1.5 }}>
                                <Alert severity="error">{structuresError}</Alert>
                            </Box>
                        ) : structures.length === 0 ? (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {l10n.getString("qms-templates-structures-empty", null, "Brak aktywnych struktur do wyboru.")}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ p: 1.5 }}>
                                <StructureDefinitionSelect
                                    structures={structures}
                                    value={selectedStructureId}
                                    onChange={value => setSelectedStructureId(value)}
                                    loading={isStructuresLoading}
                                    label={undefined}
                                    placeholder={l10n.getString("qms-templates-structures-placeholder", null, "Wybierz strukturę")}
                                    helperText={undefined}
                                />
                            </Box>
                        )}
                    </Paper>

                    <Paper variant="outlined" sx={{ overflow: "hidden", minHeight: 360, display: "flex", flexDirection: "column" }}>
                        {!!tree?.roots.length && (
                            <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.100" }}>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<UnfoldMoreIcon />}
                                        onClick={() => setExpanded(collectExpandableNodeIds(tree.roots))}
                                    >
                                        {l10n.getString("structure-expand-all", null, "Rozwiń wszystko")}
                                    </Button>

                                    <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<UnfoldLessIcon />}
                                        onClick={() => setExpanded([])}
                                    >
                                        {l10n.getString("structure-collapse-all", null, "Zwiń wszystko")}
                                    </Button>
                                </Stack>
                            </Box>
                        )}

                        <Box sx={{ p: 1.5, flex: 1 }}>
                            {isTreeLoading ? (
                                <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ height: "100%", minHeight: 220 }}>
                                    <CircularProgress size={28} />
                                    <Typography variant="body2" color="text.secondary">
                                        {l10n.getString("qms-templates-tree-loading", null, "Ładowanie drzewa struktury...")}
                                    </Typography>
                                </Stack>
                            ) : treeError ? (
                                <Alert severity="error">{treeError}</Alert>
                            ) : !selectedStructure ? (
                                <Typography variant="body2" color="text.secondary">
                                    {l10n.getString("qms-templates-tree-no-structure", null, "Najpierw wybierz strukturę z listy.")}
                                </Typography>
                            ) : !tree || tree.roots.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    {l10n.getString("qms-templates-tree-empty", null, "Wybrana struktura nie ma jeszcze zdefiniowanego drzewa.")}
                                </Typography>
                            ) : (
                                <Box
                                    sx={{
                                        "& .MuiTreeItem-root": { my: 0.125 },
                                        "& .MuiTreeItem-content": { py: 0.125, pr: 0.5, pl: 0.25, minHeight: 32, borderRadius: 1 },
                                        "& .MuiTreeItem-groupTransition": { ml: 1.5 },
                                        "& .MuiTreeItem-iconContainer": { mr: 0.5 },
                                    }}
                                >
                                    <SimpleTreeView
                                        expandedItems={expanded}
                                        onExpandedItemsChange={(_, itemIds) => setExpanded(itemIds as string[])}
                                        selectedItems={selectedNodeId ?? ""}
                                        onSelectedItemsChange={(_, itemId) => setSelectedNodeId((itemId as string) ?? null)}
                                    >
                                        {tree.roots.map(root => (
                                            <StructureTreeNodeItem key={root.id} node={root} />
                                        ))}
                                    </SimpleTreeView>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Stack>

                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                    <Paper variant="outlined" sx={{ minWidth: 0, p: 0, overflow: "hidden" }}>
                        <Box sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.100" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {l10n.getString("qms-structure-direct-documents", null, "Dokumenty przypisane do wybranego elementu")}
                            </Typography>
                        </Box>

                        <TableContainer>
                            <DataGrid
                                style={{ minHeight: 100, height: 320 }}
                                localeText={getCustomLocaleText()}
                                rows={currentNodeDocuments}
                                getRowId={row => row.documentId}
                                columns={currentNodeColumns}
                                loading={isLoadingDocuments}
                                slots={{ toolbar: QuickSearchToolbar }}
                            />

                            <Box sx={{ display: "flex", alignItems: "center", p: 1, minHeight: 44 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {!selectedNode
                                        ? l10n.getString("qms-templates-tree-no-structure", null, "Najpierw wybierz strukturę z listy.")
                                        : currentNodeDocuments.length === 0 && !isLoadingDocuments
                                            ? l10n.getString("qms-structure-direct-documents-empty", null, "Brak dokumentów przypisanych bezpośrednio do wybranego elementu.")
                                            : ""}
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "center", px: 1, pb: 2, mt: 1 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateQMSDocument(true)}
                                    disabled={!selectedNode}
                                    sx={{ minWidth: 220 }}
                                >
                                    {l10n.getString("add-document", null, "Add document")}
                                </Button>
                            </Box>
                        </TableContainer>
                    </Paper>

                    <Paper variant="outlined" sx={{ minWidth: 0, p: 0, overflow: "hidden" }}>
                        <Box sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.100" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {l10n.getString("qms-structure-inherited-documents", null, "Dokumenty dziedziczone")}
                            </Typography>
                        </Box>

                        <TableContainer>
                            <DataGrid
                                style={{ minHeight: 100, height: 320 }}
                                localeText={getCustomLocaleText()}
                                rows={inheritedDocuments}
                                getRowId={row => row.documentId}
                                columns={inheritedColumns}
                                loading={isLoadingDocuments}
                                slots={{ toolbar: QuickSearchToolbar }}
                            />

                            <Box sx={{ p: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {!selectedNode
                                        ? l10n.getString("qms-templates-tree-no-structure", null, "Najpierw wybierz strukturę z listy.")
                                        : inheritedDocuments.length === 0 && !isLoadingDocuments
                                            ? l10n.getString("qms-structure-inherited-documents-empty", null, "Brak dokumentów dziedziczonych z innych elementów struktury.")
                                            : ""}
                                </Typography>
                            </Box>
                        </TableContainer>
                    </Paper>
                </Stack>
            </Stack>

            <CreateDocumentQMSDialog
                open={createQMSDocument}
                selectedStructureNode={selectedNode ? { id: selectedNode.id, code: selectedNode.code, name: selectedNode.name } : null}
                onClose={() => {
                    setCreateQMSDocument(false)
                    if (selectedNodeId) {
                        loadDocumentsForNode(selectedNodeId)
                    }
                }}
            />

            <DocumentReportPrinter
                documentId={printDocumentId ?? ""}
                language=""
                mode="open"
                isDialogOpen={printDocumentId !== null}
                closeDialog={() => setPrintDocumentId(null)}
            />

            <Dialog
                open={statsDocument !== null}
                onClose={() => {
                    if (!isStatsLoading) {
                        setStatsDocument(null)
                        setDocumentStats(null)
                        setStatsError(null)
                    }
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {l10n.getString("document-download-stats", null, "Statystyki pobrań")}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {statsDocument && (
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {statsDocument.documentNumber}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {statsDocument.title}
                                </Typography>
                            </Box>
                        )}

                        {isStatsLoading ? (
                            <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
                                <CircularProgress size={28} />
                                <Typography variant="body2" color="text.secondary">
                                    {l10n.getString("document-download-stats-loading", null, "Ładowanie statystyk pobrań...")}
                                </Typography>
                            </Stack>
                        ) : statsError ? (
                            <Alert severity="error">{statsError}</Alert>
                        ) : documentStats && (
                            <Stack spacing={2}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                    <Paper variant="outlined" sx={{ flex: 1, p: 2, bgcolor: "grey.50" }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {l10n.getString("document-download-stats-total", null, "Łączna liczba pobrań")}
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700}>
                                            {documentStats.totalDownloadCount}
                                        </Typography>
                                    </Paper>
                                    <Paper variant="outlined" sx={{ flex: 1, p: 2, bgcolor: "grey.50" }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {l10n.getString("document-download-stats-unique-users", null, "Liczba unikalnych użytkowników")}
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700}>
                                            {documentStats.uniqueUsersCount}
                                        </Typography>
                                    </Paper>
                                </Stack>

                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                        {l10n.getString("document-download-stats-users", null, "Użytkownicy")}
                                    </Typography>

                                    {documentStats.users.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {l10n.getString("document-download-stats-users-empty", null, "Brak danych o pobraniach dla użytkowników.")}
                                        </Typography>
                                    ) : (
                                        <Paper variant="outlined">
                                            {documentStats.users.map((user, index) => (
                                                <Box key={`${user.userId}-${index}`}>
                                                    {index > 0 && <Divider />}
                                                    <Stack
                                                        direction={{ xs: "column", sm: "row" }}
                                                        spacing={1}
                                                        justifyContent="space-between"
                                                        sx={{ p: 1.5 }}
                                                    >
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={600} noWrap>
                                                                {user.userName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {user.userId}
                                                            </Typography>
                                                        </Box>
                                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {l10n.getString("document-download-stats-user-count", null, "Pobrania")}
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {user.downloadCount}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {l10n.getString("document-download-stats-user-last-date", null, "Ostatnie pobranie")}
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {user.lastDownloadDate ? new Date(user.lastDownloadDate).toLocaleString() : l10n.getString("not-applicable-short", null, "N/A")}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Paper>
                                    )}
                                </Box>
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <CancelButton
                        loading={false}
                        onClick={() => {
                            setStatsDocument(null)
                            setDocumentStats(null)
                            setStatsError(null)
                        }}
                    />
                </DialogActions>
            </Dialog>
        </>
    )
}